import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { toast } from "sonner";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Pencil, Check, X as XIcon, Trash2 } from "lucide-react";

// Point pdf.js at the worker copy served from /public.
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const PdfViewer = ({ file, blobUrl, onSaved }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [editing, setEditing] = useState(false);
  const [annotations, setAnnotations] = useState([]); // { id, pageIndex, xPct, yPct, text }
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);
  const pageWrapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const el = containerRef.current;
    const measure = () => setContainerWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pageWidth = Math.max(280, Math.floor(containerWidth * scale) - 24);

  const addAnnotation = (evt) => {
    if (!editing || !pageWrapRef.current) return;
    const rect = pageWrapRef.current.getBoundingClientRect();
    const xPct = (evt.clientX - rect.left) / rect.width;
    const yPct = (evt.clientY - rect.top) / rect.height;
    const id = Math.random().toString(36).slice(2);
    setAnnotations((a) => [...a, { id, pageIndex, xPct, yPct, text: "" }]);
    setEditingId(id);
  };

  const updateText = (id, text) => {
    setAnnotations((a) => a.map((n) => (n.id === id ? { ...n, text } : n)));
  };

  const deleteAnnotation = (id) => {
    setAnnotations((a) => a.filter((n) => n.id !== id));
    if (editingId === id) setEditingId(null);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await fetch(blobUrl);
      const bytes = new Uint8Array(await res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(bytes);
      const helv = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const pages = pdfDoc.getPages();
      annotations.forEach((n) => {
        if (!n.text.trim()) return;
        const p = pages[n.pageIndex];
        if (!p) return;
        const { width, height } = p.getSize();
        p.drawText(n.text, {
          x: n.xPct * width,
          y: height - n.yPct * height - 12,
          size: 12,
          font: helv,
          color: rgb(0.05, 0.05, 0.05),
        });
      });
      const out = await pdfDoc.save();
      const fd = new FormData();
      fd.append("file", new Blob([out], { type: "application/pdf" }), file.name);
      await api.post(`/files/${file.file_id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Changes saved");
      setAnnotations([]);
      setEditing(false);
      setEditingId(null);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error("Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setAnnotations([]);
    setEditing(false);
    setEditingId(null);
  };

  const pageAnnotations = annotations.filter((n) => n.pageIndex === pageIndex);

  return (
    <div className="flex h-full flex-col bg-[#525659]" data-testid="pdf-viewer">
      <div className="flex flex-wrap items-center justify-center gap-2 border-b border-black/30 bg-[#1a1a1a] px-3 py-2 text-white">
        <button onClick={() => setPageIndex((p) => Math.max(0, p - 1))} disabled={pageIndex === 0} data-testid="pdf-prev" className="flex items-center gap-1 rounded border border-white/20 px-2 py-1 text-xs hover:bg-white/10 disabled:opacity-40">
          <ChevronLeft className="h-3.5 w-3.5" /> Prev
        </button>
        <span className="font-mono text-xs text-white/80" data-testid="pdf-page">Page {pageIndex + 1} / {numPages || "…"}</span>
        <button onClick={() => setPageIndex((p) => Math.min((numPages || 1) - 1, p + 1))} disabled={pageIndex + 1 >= numPages} data-testid="pdf-next" className="flex items-center gap-1 rounded border border-white/20 px-2 py-1 text-xs hover:bg-white/10 disabled:opacity-40">
          Next <ChevronRight className="h-3.5 w-3.5" />
        </button>
        <span className="mx-1 h-4 w-px bg-white/20" />
        <button onClick={() => setScale((s) => Math.max(0.5, s - 0.15))} data-testid="pdf-zoom-out" className="rounded border border-white/20 p-1 hover:bg-white/10">
          <ZoomOut className="h-3.5 w-3.5" />
        </button>
        <span className="w-10 text-center font-mono text-[11px] text-white/70">{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale((s) => Math.min(3, s + 0.15))} data-testid="pdf-zoom-in" className="rounded border border-white/20 p-1 hover:bg-white/10">
          <ZoomIn className="h-3.5 w-3.5" />
        </button>
        <span className="mx-1 h-4 w-px bg-white/20" />
        {!editing && (
          <button onClick={() => setEditing(true)} data-testid="pdf-edit-btn" className="flex items-center gap-1 rounded bg-[#0364b8] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#0358a0]">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
        {editing && (
          <>
            <span className="hidden font-mono text-[10px] uppercase tracking-widest text-emerald-300 sm:inline">Tap page to add text</span>
            <button onClick={save} disabled={saving} data-testid="pdf-save-btn" className="flex items-center gap-1 rounded bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-60">
              <Check className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={cancelEdit} disabled={saving} data-testid="pdf-cancel-edit-btn" className="flex items-center gap-1 rounded border border-white/20 px-2.5 py-1 text-xs text-white hover:bg-white/10">
              <XIcon className="h-3.5 w-3.5" /> Cancel
            </button>
          </>
        )}
      </div>
      <div ref={containerRef} className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mx-auto flex flex-col items-center">
          <Document
            file={blobUrl}
            onLoadSuccess={(d) => setNumPages(d.numPages)}
            loading={<div className="p-8 text-sm text-white/70">Loading PDF…</div>}
            error={<div className="p-8 text-sm text-red-300">Could not open PDF</div>}
          >
            <div ref={pageWrapRef} onClick={addAnnotation} className={`relative ${editing ? "cursor-crosshair" : ""}`}>
              <Page pageNumber={pageIndex + 1} width={pageWidth} renderTextLayer={!editing} renderAnnotationLayer={false} />
              {pageAnnotations.map((n) => (
                <AnnotationBox
                  key={n.id}
                  ann={n}
                  editing={editingId === n.id}
                  onFocus={() => setEditingId(n.id)}
                  onChange={(t) => updateText(n.id, t)}
                  onDelete={() => deleteAnnotation(n.id)}
                />
              ))}
            </div>
          </Document>
        </div>
      </div>
    </div>
  );
};

const AnnotationBox = ({ ann, editing, onFocus, onChange, onDelete }) => {
  const stop = (e) => e.stopPropagation();
  return (
    <div
      className="absolute z-10 flex items-start gap-1"
      style={{ left: `${ann.xPct * 100}%`, top: `${ann.yPct * 100}%` }}
      onClick={stop}
    >
      <input
        autoFocus={editing}
        value={ann.text}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        placeholder="Type here"
        data-testid={`pdf-annotation-${ann.id}`}
        className="min-w-[80px] rounded-sm border border-emerald-400 bg-white/95 px-1.5 py-0.5 text-xs text-gray-900 shadow-md focus:outline-none focus:ring-1 focus:ring-emerald-500"
      />
      <button onClick={onDelete} aria-label="Delete annotation" className="rounded bg-white/95 p-0.5 text-red-500 shadow-sm hover:bg-red-50">
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
};

export default PdfViewer;
